import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import { useUserStore } from '@/stores/userStore'
import { useGroupStore } from '@/stores/groupStore'
import {
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  Skeleton,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Menu,
  MenuItem,
  Breadcrumbs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
} from '@mui/material'
import Link from '@mui/material/Link'
import { Edit as EditIcon, Delete as DeleteIcon, MoreVert as MoreVertIcon, LockReset as LockResetIcon, Home as HomeIcon, NavigateNext as NavigateNextIcon, ChatBubbleOutline as ChatIcon } from '@mui/icons-material'
import { Link as RouterLink } from 'react-router-dom'
import { ErrorPage } from '@/components/common/ErrorPage'
import { format } from 'date-fns'
import { usersApi } from '@/api/users'
import { ContractInfo } from '@/components/contracts/ContractInfo'
import { ContractForm } from '@/components/contracts/ContractForm'
import { StudentFormSummary } from '@/components/studentForm/StudentFormSummary'
import { StudentSelfPracticeCard } from '@/components/users/StudentSelfPracticeCard'
import { StudentAbsenceCard } from '@/components/users/StudentAbsenceCard'
import { ROUTES } from '@/utils/constants'
import type { Contract, UpdateUserData } from '@/types'
import { formatDateDMY } from '@/utils/date'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import '@/styles/dashboard.css'

export function UserDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const {
    currentUser,
    isLoading,
    error,
    errorStatus,
    fetchUser,
    updateUser,
    deleteUser,
    clearError,
    invalidateUsersList,
  } = useUserStore()
  const { groups, fetchGroups } = useGroupStore()

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [passwordResetMessage, setPasswordResetMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    if (id) {
      fetchUser(parseInt(id)).catch((err) => {
        console.error('Failed to fetch user:', err)
      })
      fetchGroups().catch((err) => {
        console.error('Failed to fetch groups:', err)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const handleEdit = () => {
    setMenuAnchor(null)
    setIsEditModalOpen(true)
  }

  const handleDelete = async () => {
    setMenuAnchor(null)
    if (!currentUser) return
    if (window.confirm(t('users.deleteConfirm'))) {
      try {
        await deleteUser(currentUser.id)
        navigate(ROUTES.USERS)
      } catch (err) {
        // Error handled in store
      }
    }
  }

  const handleSendPasswordReset = async () => {
    setMenuAnchor(null)
    if (!currentUser) return
    try {
      await usersApi.sendPasswordReset(currentUser.id)
      setPasswordResetMessage({ type: 'success', text: t('users.sendPasswordResetSuccess') })
      setTimeout(() => setPasswordResetMessage(null), 5000)
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string }
      setPasswordResetMessage({
        type: 'error',
        text: error?.response?.data?.message || error?.message || t('common.error'),
      })
      setTimeout(() => setPasswordResetMessage(null), 5000)
    }
  }

  const handleUpdateContract = async (data: UpdateUserData) => {
    if (!currentUser) return
    try {
      await updateUser(currentUser.id, data)
      setIsEditModalOpen(false)
      fetchUser(currentUser.id)
      if (data.contract_status !== undefined) {
        invalidateUsersList()
      }
    } catch (err) {
      // Error handled in store
    }
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Skeleton variant="text" width={200} height={40} />
          </Box>
          <Card>
            <CardContent>
              <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
              <Skeleton variant="text" width="100%" height={24} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="80%" height={24} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="60%" height={24} />
            </CardContent>
          </Card>
        </Box>
      </DashboardLayout>
    )
  }

  const contractHistory: Contract[] = currentUser?.contracts ?? []

  if (!currentUser) {
    return (
      <ErrorPage
        fullPage
        statusCode={errorStatus ?? 500}
        message={error ?? undefined}
      />
    )
  }

  const studentDisplayName =
    currentUser.surname || currentUser.first_name
      ? [currentUser.surname, currentUser.first_name].filter(Boolean).join(' ')
      : currentUser.email

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link component={RouterLink} to={ROUTES.DASHBOARD} underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Link component={RouterLink} to={ROUTES.USERS} underline="hover">
            {t('dashboard.students')}
          </Link>
          <Typography color="text.primary">{studentDisplayName || t('profile.title')}</Typography>
        </Breadcrumbs>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('profile.title')}
          </Typography>
          <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <MoreVertIcon />
          </IconButton>
          <PageHelpPopover pageKey="users" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}
        {passwordResetMessage && (
          <Alert
            severity={passwordResetMessage.type}
            sx={{ mb: 2 }}
            onClose={() => setPasswordResetMessage(null)}
          >
            {passwordResetMessage.text}
          </Alert>
        )}

        {/* Информация о пользователе */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('profile.personalInfo')}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('profile.email')}
                </Typography>
                <Typography variant="body1">
                  {currentUser.email}
                </Typography>
              </Box>

              {currentUser.nickname && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('users.nickname')}
                  </Typography>
                  <Typography variant="body1">
                    {currentUser.nickname}
                  </Typography>
                </Box>
              )}

              {currentUser.first_name && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('profile.firstName')}
                  </Typography>
                  <Typography variant="body1">
                    {currentUser.first_name}
                  </Typography>
                </Box>
              )}

              {currentUser.surname && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('profile.surname')}
                  </Typography>
                  <Typography variant="body1">
                    {currentUser.surname}
                  </Typography>
                </Box>
              )}

              {currentUser.middle_name && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('profile.middleName')}
                  </Typography>
                  <Typography variant="body1">
                    {currentUser.middle_name}
                  </Typography>
                </Box>
              )}

              {currentUser.birthday && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('profile.birthday')}
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(currentUser.birthday), 'dd.MM.yyyy')}
                  </Typography>
                </Box>
              )}

              {currentUser.created_at && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('users.registeredAt')}
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(currentUser.created_at), 'dd.MM.yyyy')}
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Самопрактика ученика: факт и объём, без разбора попыток. Данные
            берутся из журнала активности — событие exercise.trained.
            Только на профиле ученика: у учителя таких работ не бывает,
            и блок был бы вечно пустым. */}
        {currentUser.roles?.some((role) => role.slug === 'student') ? (
          <>
            {/* Отпуск и каникулы: тот же период, что двигает автогенерацию
                уроков. Договор при этом остаётся активным. */}
            <StudentAbsenceCard
              studentId={currentUser.id}
              onChanged={() => {
                if (id) void fetchUser(parseInt(id)).catch(() => {})
              }}
            />
            <StudentSelfPracticeCard studentId={currentUser.id} />
          </>
        ) : null}

        {/* Блок «Договор»: один блок, контент договора(ов) внутри; при нескольких — разделитель */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                {t('contracts.title')}
              </Typography>
              <IconButton size="small" onClick={handleEdit}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
            {currentUser.contract ? (
              <ContractInfo contract={currentUser.contract} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('users.noContract')}
              </Typography>
            )}

            {contractHistory.length > 1 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                  {t('contracts.historyTitle', 'История договоров')}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('contracts.status', 'Статус')}</TableCell>
                        <TableCell>{t('contracts.period', 'Период')}</TableCell>
                        <TableCell>{t('contracts.group', 'Группа')}</TableCell>
                        <TableCell>{t('contracts.notes', 'Заметка')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {contractHistory.map((contract) => (
                        <TableRow
                          key={contract.id}
                          sx={contract.is_active ? { bgcolor: 'action.hover' } : undefined}
                        >
                          <TableCell>
                            <Chip
                              size="small"
                              label={contract.status_label}
                              color={contract.is_active ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            {formatDateDMY(contract.starts_at)}
                            {contract.expires_at ? ` — ${formatDateDMY(contract.expires_at)}` : ''}
                          </TableCell>
                          <TableCell>{contract.group?.name ?? '—'}</TableCell>
                          <TableCell>{contract.notes?.trim() || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </CardContent>
        </Card>

        {/* Анкета ученика — по договору, поэтому строго под блоком договора */}
        {currentUser.contract && (
          <StudentFormSummary contractId={currentUser.contract.id} canManage />
        )}

        {/* Меню действий — открывается вправо */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <MenuItem onClick={handleEdit}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            {t('users.edit')}
          </MenuItem>
          {id && (
            <MenuItem
              onClick={() => {
                setMenuAnchor(null)
                navigate(ROUTES.CHAT_DIRECT(parseInt(id)))
              }}
            >
              <ChatIcon sx={{ mr: 1 }} fontSize="small" />
              {t('chat.openChat')}
            </MenuItem>
          )}
          <MenuItem onClick={handleSendPasswordReset}>
            <LockResetIcon sx={{ mr: 1 }} fontSize="small" />
            {t('users.sendPasswordReset')}
          </MenuItem>
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            {t('users.delete')}
          </MenuItem>
        </Menu>

        {/* Модальное окно редактирования договора */}
        <Dialog
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('users.edit')}</DialogTitle>
          <DialogContent>
            <ContractForm
              showUserFields={!!currentUser.contract}
              initialValues={{
                surname: currentUser.surname ?? '',
                first_name: currentUser.first_name ?? '',
                middle_name: currentUser.middle_name ?? '',
                email: currentUser.email ?? '',
                birthday: currentUser.birthday ?? '',
                nickname: currentUser.nickname ?? '',
                ...(currentUser.contract ? {
                  contract_status: currentUser.contract.status,
                  contract_group_id: currentUser.contract.group_id,
                  contract_starts_at: currentUser.contract.starts_at,
                  contract_expires_at: currentUser.contract.expires_at,
                  contract_notes: currentUser.contract.notes,
                } : {}),
              }}
              onSubmit={handleUpdateContract}
              onCancel={() => setIsEditModalOpen(false)}
              isLoading={isLoading}
              availableGroups={groups.map((g) => ({ id: g.id, name: g.name }))}
            />
          </DialogContent>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}
